import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { Reveal, RevealGroup, RevealItem } from "@/lib/motion";
import { Section, SectionHeading } from "./primitives";
import { TOKENOMICS, VESTING } from "@/config/site";

export default function TokenomicsSection() {
  return (
    <Section id="tokenomics">
      <SectionHeading
        eyebrow="Distribution"
        title="Tokenomics"
        lead="A transparent allocation across community, liquidity, ecosystem, staking, treasury and team."
      />

      <div className="mt-16 grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-16">
        {/* Donut */}
        <Reveal className="relative lg:col-span-5" data-testid="tokenomics-chart">
          <div className="relative mx-auto aspect-square w-full max-w-sm">
            <div className="absolute inset-0 rounded-full bg-indigo-600/10 blur-[80px]" />
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={TOKENOMICS}
                  dataKey="pct"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="62%"
                  outerRadius="92%"
                  paddingAngle={2}
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive
                  animationDuration={900}
                >
                  {TOKENOMICS.map((e) => (
                    <Cell key={e.name} fill={e.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gray-500">Max Supply</div>
              <div className="mt-1 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">50T</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-indigo-300">XVPR</div>
            </div>
          </div>
        </Reveal>

        {/* Allocation list / cards */}
        <RevealGroup className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:col-span-7">
          {TOKENOMICS.map((t) => (
            <RevealItem key={t.name} data-testid={`tokenomics-item-${t.pct}`}>
              <div className="group flex h-full flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-all duration-500 hover:border-white/20">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2.5">
                    <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: t.color }} />
                    <span className="font-display text-2xl font-black text-white">{t.pct}%</span>
                  </span>
                </div>
                <div className="font-display text-xs font-bold uppercase leading-tight tracking-tight text-white">
                  {t.name}
                </div>
                <div className="font-mono text-[11px] tracking-tight text-gray-500">{t.amount}</div>
                <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full" style={{ width: `${t.pct}%`, backgroundColor: t.color }} />
                </div>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>

      {/* Vesting */}
      <div className="mt-28">
        <SectionHeading eyebrow="Vesting" title="Long-Term Alignment" />
        <RevealGroup className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {VESTING.map((v, i) => (
            <RevealItem key={v.title} data-testid={`vesting-${i}`}>
              <div className="flex h-full flex-col gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <div className="font-mono text-[11px] tracking-[0.2em] text-indigo-400">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3 className="font-display text-sm font-bold uppercase tracking-tight text-white">{v.title}</h3>
                <p className="text-sm leading-relaxed text-gray-400">{v.body}</p>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </Section>
  );
}
